'use client'

import React, { useCallback, useState, type KeyboardEvent } from 'react'
import { cn } from '../../../lib/utils'
import Icon from '../Icon'
import { Button } from '../Button'
import {
  DropdownMenuComponent as DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropDownMenu'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../Drawer'
import { useMediaQuery } from '../Threads/hooks/useMediaQuery'
import {
  PageTypeaheadPanel,
  type PageTypeaheadResult,
} from '../PageTypeaheadPopover'

export type WorkspaceNavAddMenuProps = {
  ariaLabel?: string
  triggerClassName?: string
  pageTypeahead?: {
    title?: string
    placeholder?: string
    emptyMessage?: string
    onSearch: (query: string) => Promise<PageTypeaheadResult[]>
    onSelect: (page: PageTypeaheadResult) => void | Promise<void>
    onCreateNewPage?: () => void
    createNewPageLabel?: string
  }
  addSection?: {
    title?: string
    placeholder?: string
    submitLabel?: string
    onSubmit: (label: string) => void | Promise<void>
  }
  onExportWorkspace?: () => void | Promise<void>
  onImportWorkspace?: () => void | Promise<void>
}

type WorkspaceNavPanel = 'menu' | 'page' | 'section'

const menuOptionClassName =
  'w-full flex items-center gap-2 rounded-sm px-2 h-9 text-left text-[11px] text-foreground/80 hover:bg-muted/60 transition-colors'

const menuOptionIconClassName =
  'flex size-3.5 shrink-0 items-center justify-center'

function WorkspaceNavMenuOptions({
  onSelectPage,
  onSelectSection,
  onExportWorkspace,
  onImportWorkspace,
  showPage = true,
  showSection = true,
}: {
  onSelectPage: () => void
  onSelectSection: () => void
  onExportWorkspace?: () => void
  onImportWorkspace?: () => void
  showPage?: boolean
  showSection?: boolean
}) {
  return (
    <>
      {showPage ? (
        <button
          type="button"
          className={menuOptionClassName}
          onClick={onSelectPage}
        >
          <span className={menuOptionIconClassName}>
            <Icon name="FileText" size={14} />
          </span>
          Add page
        </button>
      ) : null}
      {showSection ? (
        <button
          type="button"
          className={menuOptionClassName}
          onClick={onSelectSection}
        >
          <span className={menuOptionIconClassName}>
            <Icon name="LayoutList" size={14} />
          </span>
          Add section
        </button>
      ) : null}
      {onExportWorkspace ? (
        <button
          type="button"
          className={menuOptionClassName}
          onClick={() => {
            void onExportWorkspace()
          }}
        >
          <span className={menuOptionIconClassName}>
            <Icon name="Download" size={14} />
          </span>
          Export workspace
        </button>
      ) : null}
      {onImportWorkspace ? (
        <button
          type="button"
          className={menuOptionClassName}
          onClick={() => {
            void onImportWorkspace()
          }}
        >
          <span className={menuOptionIconClassName}>
            <Icon name="Upload" size={14} />
          </span>
          Import workspace
        </button>
      ) : null}
    </>
  )
}

function WorkspaceNavSectionPanel({
  sectionName,
  placeholder,
  submitLabel,
  isSubmitting,
  onSectionNameChange,
  onSectionKeyDown,
  onSubmit,
}: {
  sectionName: string
  placeholder?: string
  submitLabel?: string
  isSubmitting: boolean
  onSectionNameChange: (value: string) => void
  onSectionKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  onSubmit: () => void
}) {
  return (
    <div className="flex flex-col gap-2 min-w-[220px]">
      <input
        type="text"
        value={sectionName}
        onChange={(event) => onSectionNameChange(event.target.value)}
        onKeyDown={onSectionKeyDown}
        placeholder={placeholder ?? 'Section name'}
        disabled={isSubmitting}
        autoFocus
        className={cn(
          'w-full h-8 rounded-md border border-border/60 bg-background px-2',
          'text-xs text-foreground placeholder:text-muted-foreground/50',
          'outline-none focus-visible:ring-1 focus-visible:ring-ring',
        )}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        label={submitLabel ?? 'Add section'}
        isDisabled={!sectionName.trim() || isSubmitting}
        isLoading={isSubmitting}
        handleClick={onSubmit}
      />
    </div>
  )
}

export function WorkspaceNavAddMenu({
  ariaLabel = 'Add to workspace',
  triggerClassName,
  pageTypeahead,
  addSection,
  onExportWorkspace,
  onImportWorkspace,
}: WorkspaceNavAddMenuProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const hasPageMenu = Boolean(pageTypeahead)
  const hasSectionMenu = Boolean(addSection)
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState<WorkspaceNavPanel>('menu')
  const [sectionName, setSectionName] = useState('')
  const [isSubmittingSection, setIsSubmittingSection] = useState(false)

  const resetMenu = useCallback(() => {
    setOpen(false)
    setPanel('menu')
    setSectionName('')
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setPanel('menu')
      setSectionName('')
    }
  }, [])

  const handleSectionSubmit = useCallback(async () => {
    const trimmed = sectionName.trim()
    if (!trimmed || isSubmittingSection || !addSection) return

    setIsSubmittingSection(true)
    try {
      await addSection.onSubmit(trimmed)
      resetMenu()
    } finally {
      setIsSubmittingSection(false)
    }
  }, [addSection, isSubmittingSection, resetMenu, sectionName])

  const handleSectionKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        void handleSectionSubmit()
      }
    },
    [handleSectionSubmit],
  )

  const panelTitle =
    panel === 'page'
      ? pageTypeahead?.title ?? 'Add page'
      : panel === 'section'
        ? addSection?.title ?? 'Add section'
        : ariaLabel

  const triggerButton = (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        'text-muted-foreground/60 text-xs rounded-lg p-1 hover:bg-accent/50',
        'opacity-0 transition-opacity group-hover/section-header:opacity-100 group-focus-within/section-header:opacity-100',
        triggerClassName,
      )}
    >
      <Icon name="Ellipsis" />
    </button>
  )

  const renderPanelContent = () => (
    <>
      {panel === 'menu' ? (
        <WorkspaceNavMenuOptions
          showPage={hasPageMenu}
          showSection={hasSectionMenu}
          onSelectPage={() => setPanel('page')}
          onSelectSection={() => setPanel('section')}
          onExportWorkspace={
            onExportWorkspace
              ? () => {
                  resetMenu()
                  void onExportWorkspace()
                }
              : undefined
          }
          onImportWorkspace={
            onImportWorkspace
              ? () => {
                  resetMenu()
                  void onImportWorkspace()
                }
              : undefined
          }
        />
      ) : null}

      {panel === 'page' && pageTypeahead ? (
        <PageTypeaheadPanel
          placeholder={pageTypeahead.placeholder}
          emptyMessage={pageTypeahead.emptyMessage}
          onSearch={pageTypeahead.onSearch}
          onSelect={pageTypeahead.onSelect}
          onCreateNewPage={pageTypeahead.onCreateNewPage}
          createNewPageLabel={pageTypeahead.createNewPageLabel}
          onClose={resetMenu}
        />
      ) : null}

      {panel === 'section' && addSection ? (
        <WorkspaceNavSectionPanel
          sectionName={sectionName}
          placeholder={addSection.placeholder}
          submitLabel={addSection.submitLabel}
          isSubmitting={isSubmittingSection}
          onSectionNameChange={setSectionName}
          onSectionKeyDown={handleSectionKeyDown}
          onSubmit={() => {
            void handleSectionSubmit()
          }}
        />
      ) : null}
    </>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent className="max-h-[90vh] flex flex-col px-0">
          <DrawerHeader className="text-left pb-2 px-4">
            <div className="flex items-center gap-2">
              {panel !== 'menu' ? (
                <button
                  type="button"
                  aria-label="Back"
                  className="rounded-md p-1 hover:bg-accent/50 text-muted-foreground/80"
                  onClick={() => setPanel('menu')}
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
              ) : null}
              <DrawerTitle className="text-sm">{panelTitle}</DrawerTitle>
            </div>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {renderPanelContent()}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(panel === 'menu' ? 'min-w-[160px] p-1' : 'w-72 p-2')}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        {panel !== 'menu' ? (
          <div className="px-2 pb-1 text-[11px] font-medium text-muted-foreground/80">
            {panelTitle}
          </div>
        ) : null}

        {panel === 'menu' ? (
          <>
            {hasPageMenu ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs"
                onSelect={(event) => {
                  event.preventDefault()
                  setPanel('page')
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="FileText" size={14} />
                </span>
                Add page
              </DropdownMenuItem>
            ) : null}
            {hasSectionMenu ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs"
                onSelect={(event) => {
                  event.preventDefault()
                  setPanel('section')
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="LayoutList" size={14} />
                </span>
                Add section
              </DropdownMenuItem>
            ) : null}
            {onExportWorkspace ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs"
                onSelect={(event) => {
                  event.preventDefault()
                  handleOpenChange(false)
                  void onExportWorkspace()
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="Download" size={14} />
                </span>
                Export workspace
              </DropdownMenuItem>
            ) : null}
            {onImportWorkspace ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs"
                onSelect={(event) => {
                  event.preventDefault()
                  handleOpenChange(false)
                  void onImportWorkspace()
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="Upload" size={14} />
                </span>
                Import workspace
              </DropdownMenuItem>
            ) : null}
          </>
        ) : (
          renderPanelContent()
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
